import { EditorContent, useEditor } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { useEffect, useState } from "react";
import { cn } from "~/utils";
import { useOrganisations } from "~/contexts/organisations-provider";
import { logError } from "~/shared/services/error-reporting";
import { toast } from "~/utils/toast";

export const OrganisationName = ({
  id,
  name,
  isEditing,
  onChange,
}: {
  id: string;
  isEditing: boolean;
  name: string;
  onChange: () => void;
}) => {
  const { updateOrganisationName } = useOrganisations();
  const [loading, setLoading] = useState(false);

  const updateName = (value: string) => {
    setLoading(true);
    updateOrganisationName(id, value)
      .catch((error) => {
        logError(error.message);
        toast.error(error.message);
      })
      .then(() => setLoading(false));
  };

  const editor = useEditor({
    editorProps: { attributes: { role: "textbox", "aria-multiline": "true" } },
    onBlur: (ctx) => {
      if (ctx.editor.isDestroyed) {
        return;
      }

      const value = ctx.editor.getText().trim() || name;
      ctx.editor.chain().setContent(value).run();
      onChange();
      if (value !== name) {
        updateName(value);
      }
    },
    extensions: [
      Document,
      Paragraph.extend({
        addKeyboardShortcuts: () => ({
          // biome-ignore lint/correctness/noComponentHookFactories lint/correctness/noNestedComponentDefinitions: Tiptap keyboard shortcut handler, not a component
          Enter: (props) => props.editor.chain().blur().run(),
          // biome-ignore lint/correctness/noComponentHookFactories lint/correctness/noNestedComponentDefinitions: Tiptap keyboard shortcut handler, not a component
          Escape: (props) => props.editor.chain().setContent(name).blur().run(),
        }),
      }),
      Text,
    ],
    content: name,
    editable: isEditing || loading,
  });

  useEffect(() => {
    if (isEditing && !editor?.isFocused) {
      editor?.chain().focus("end").run();
    }
  }, [isEditing, editor]);

  useEffect(() => {
    if (!editor || editor.isDestroyed || editor.getText() === name) {
      return;
    }

    editor.chain().setContent(name).run();
  }, [name, editor]);

  return (
    <div className="flex items-center">
      <EditorContent
        editor={editor}
        className={cn("transition", editor?.isFocused && "bg-primary-25")}
      />
    </div>
  );
};
